import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Mail, MessageCircle, ExternalLink, ChevronDown } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    question: "How do I start a conversation with Therappi®?",
    answer: "Simply tap the chat icon on your dashboard or navigate to the Therappi® section. You can start typing your thoughts, feelings, or questions and Therappi® will respond with supportive guidance."
  },
  {
    question: "Is my data private and secure?",
    answer: "Yes, your privacy is our top priority. All conversations are encrypted and stored securely. You can export or delete your data at any time from Settings → Privacy."
  },
  {
    question: "How does Therappi® remember our conversations?",
    answer: "Therappi® uses an advanced memory system that summarises key themes and insights from your conversations. This allows for personalised support that builds on your previous sessions."
  },
  {
    question: "What is the WHOOP integration?",
    answer: "If you have a WHOOP device, you can connect it to track your physiological data alongside your emotional wellbeing. This helps provide more holistic insights."
  },
  {
    question: "How do I manage my notifications?",
    answer: "Go to Settings → Notifications to customise when and how you receive reminders and updates from the app."
  },
  {
    question: "Can I use the app offline?",
    answer: "Some features require an internet connection, but you can access previously loaded content and some journaling features offline."
  }
];

export default function AppSupport() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-primary" />
            Therappi® App Support
          </CardTitle>
          <CardDescription>
            Get help with your Therappi® experience
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertDescription>
              We're here to help with any questions about Therappi®, your account, data, or technical issues.
            </AlertDescription>
          </Alert>

          <div className="space-y-3">
            <div className="p-4 border rounded-lg space-y-2">
              <h4 className="font-semibold text-sm flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email Support
              </h4>
              <p className="text-xs text-muted-foreground">
                For general enquiries, technical issues, or account questions
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-2"
                onClick={() => window.location.href = 'mailto:support@therappi.app?subject=Therappi App Support'}
              >
                <Mail className="h-4 w-4" />
                support@therappi.app
              </Button>
            </div>
          </div>

          <div className="pt-4 border-t text-xs text-muted-foreground space-y-2">
            <p><strong>Response Time:</strong> We typically respond within 72 hours</p>
            <p><strong>Data Requests:</strong> GDPR/CCPA data access requests are processed within 30 days</p>
            <p><strong>Security Issues:</strong> Report security concerns immediately to support@therappi.app</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Frequently Asked Questions</CardTitle>
          <CardDescription>
            Quick answers to common questions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left text-sm">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>WHOOP Integration Support</CardTitle>
          <CardDescription>
            Specific help for WHOOP device connection
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="bg-muted/30 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Common WHOOP Issues</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• <strong>Connection issues?</strong> Check your WHOOP API credentials are correct</li>
              <li>• <strong>Missing data?</strong> Make sure you clicked "Always allow" when connecting</li>
              <li>• <strong>Data not syncing?</strong> Try disconnecting and reconnecting your account</li>
            </ul>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2"
            onClick={() => window.open('https://developer.whoop.com', '_blank')}
          >
            <ExternalLink className="h-4 w-4" />
            Visit WHOOP Developer Portal
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
